import React, { useState, useCallback } from "react";
import { Crown, LogOut, Volume2, VolumeX, Info, X, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import VoiceChat, { loadVoiceListen, saveVoiceListen } from "../VoiceChat";
import { VOICE_CHAT } from "../../lib/features";
import { LanguageSwitch } from "../LanguageSwitch";
import { LANG_SWITCH, t, tRich, useLang } from "../../lib/i18n";

function ParamRow({ label, value }) {
  return (
    <>
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-100 font-semibold text-end">{value}</span>
    </>
  );
}

function GameParamsPanel({ game }) {
  const nefrechOn = game.nefrech_fausse_penalty_enabled ?? true;
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[10px] font-bold uppercase tracking-widest text-amber-400/80 mb-1">
        {t("top.paramsTitle")}
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 text-xs">
        <ParamRow label={t("top.thresholdInit")} value={t("common.pts", { n: game.threshold_x ?? "—" })} />
        <ParamRow label={t("top.elimScore")}     value={t("common.pts", { n: game.elim_score_y ?? "—" })} />
        <ParamRow label={t("common.jokers")}     value={game.num_jokers ?? "—"} />
        <ParamRow label={t("top.pureSeqReq")}    value={
          (game.require_pure_sequence ?? true)
            ? <Check className="h-3.5 w-3.5 text-emerald-400 inline" />
            : <X className="h-3.5 w-3.5 text-slate-500 inline" />
        } />
      </div>

      <div className="border-t border-slate-700/60 pt-2 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 text-xs">
        <ParamRow label={t("top.penStd")}        value={t("common.pts", { n: game.standard_penalty ?? 50 })} />
        <ParamRow label={t("top.jokerHand")}     value={t("common.pts", { n: game.joker_in_hand_penalty ?? 50 })} />
        <ParamRow label={t("top.usefulDiscard")} value={t("common.pts", { n: game.useful_discard_penalty ?? 50 })} />
        <ParamRow label={t("top.continuer")}     value={t("common.pts", { n: game.continuer_penalty ?? 50 })} />
        <ParamRow label={t("top.nefrechFausse")} value={
          nefrechOn
            ? t("common.pts", { n: game.nefrech_fausse_penalty ?? 50 })
            : <span className="text-slate-500">{t("top.disabled")}</span>
        } />
      </div>

      {/* LTR in every language (the popover mirrors in Tunisian): the switch stays at the
          end of the row, under the finger that tapped it. */}
      {LANG_SWITCH && (
        <div dir="ltr" className="border-t border-slate-700/60 pt-2 flex items-center justify-between text-xs">
          <span className="text-slate-400">{t("lang.label")}</span>
          <LanguageSwitch />
        </div>
      )}
    </div>
  );
}

export const GameTopBar = ({
  game,
  me,
  pendingGroups,
  totalPendingPoints,
  muted,
  toggleMute,
  wsRef,
  wsVersion,
  user,
  onExit,
  voiceSpeaking,
  onVoiceSpeakingChange,
  onVoiceLinksChange,
}) => {
  const [paramsOpen,      setParamsOpen]      = useState(false);
  // Hearing the other players: off until asked for, then remembered.
  const [voiceInputMuted, setVoiceInputMuted] = useState(() => !loadVoiceListen());
  const { dir } = useLang();

  const handleVoiceSpeaking = useCallback((s) => onVoiceSpeakingChange?.(s), [onVoiceSpeakingChange]);

  const setListening = useCallback((on) => {
    setVoiceInputMuted(!on);
    saveVoiceListen(on);
  }, []);
  const handleToggleSound = () => setListening(voiceInputMuted);
  // Switching the mic on means joining the conversation: listen too.
  const handleMicOn = useCallback(() => setListening(true), [setListening]);

  return (
    <div
      className="safe-top px-2 sm:px-3 py-2 flex items-center justify-between border-b border-white/5 bg-slate-950/80 backdrop-blur-sm shrink-0 gap-1 relative"
      data-testid="game-top-bar"
    >
      {/* LEFT: Manche + Seuil */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        <div className="hidden sm:flex items-center gap-1.5 text-amber-300 text-sm">
          <Crown className="h-4 w-4" />
          <span className="font-display italic">{t("common.brand")}</span>
        </div>
        <div
          className="rami-glass rounded-full px-2 py-1 text-amber-300 text-[10px] sm:text-xs flex items-center gap-1"
          data-testid="round-badge"
        >
          <Crown className="h-3 w-3 sm:hidden" />
          <span dir={dir}>{tRich("top.round", { n: <b>{game.round_number}</b> })}</span>
        </div>

        {/* Seuil + pose en cours — visible pour tous les joueurs.
            Joueur actif : totalPendingPoints (local).
            Observateurs : preview_state du joueur courant (broadcasté). */}
        {(() => {
          const cpPlayer = (game.players || []).find(p => p.user_id === game.current_player_id);
          const previewGroups = cpPlayer?.preview_state?.groups || [];
          // Mirror backend combination_points: Jokers take the value of the rank they replace.
          const _rv = { '2':2,'3':3,'4':4,'5':5,'6':6,'7':7,'8':8,'9':9,'10':10,J:10,Q:10,K:10 };
          const _lo = ['A','2','3','4','5','6','7','8','9','10','J','Q','K']; // ace-low order
          const _hi = ['2','3','4','5','6','7','8','9','10','J','Q','K','A']; // ace-high order
          const _srv = (r, hi) => r === 'A' ? (hi ? 10 : 1) : (_rv[r] || 0);
          const groupPts = (g) => {
            const cs = g.cards || [];
            if (!cs.length) return 0;
            if (g.kind === 'set') {
              const a = cs.find(c => !c.is_joker);
              return a ? (a.rank === 'A' ? 10 : (_rv[a.rank] || 0)) * cs.length : 0;
            }
            if (g.kind === 'sequence') {
              const nj = cs.filter(c => !c.is_joker), jk = cs.length - nj.length;
              if (!nj.length) return 0;
              const tries = nj.some(c => c.rank === 'A') ? [[false,_lo],[true,_hi]] : [[false,_lo]];
              for (const [hi, ord] of tries) {
                const pos = nj.map(c => ord.indexOf(c.rank)).filter(i => i >= 0).sort((a,b) => a-b);
                if (pos.length !== nj.length) continue;
                const span = pos[pos.length-1] - pos[0] + 1;
                const gaps = span - nj.length;
                if (gaps > jk || span + (jk - gaps) !== cs.length) continue;
                const extra = jk - gaps;
                for (let l = 0; l <= extra; l++) {
                  const s = pos[0]-l, e = pos[pos.length-1]+(extra-l);
                  if (s >= 0 && e < ord.length) {
                    let t = 0; for (let p = s; p <= e; p++) t += _srv(ord[p], hi); return t;
                  }
                }
              }
              return 0;
            }
            return 0;
          };
          const previewPts = previewGroups.reduce((t, g) => t + groupPts(g), 0);
          const displayPts = pendingGroups?.length > 0 ? totalPendingPoints : previewPts;
          const showCounter = pendingGroups?.length > 0 || previewGroups.length > 0;
          return (
            <div
              className="rami-glass rounded-full px-2 py-1 text-amber-300 text-[10px] sm:text-xs flex items-center gap-1"
              data-testid="seuil-badge"
              dir={showCounter ? "ltr" : dir}
            >
              {showCounter ? (
                <>
                  <span
                    data-testid="pending-counter"
                    className={`font-bold ${displayPts >= game.current_threshold ? "text-emerald-300" : "text-rose-300"}`}
                  >
                    {displayPts}
                  </span>
                  <span className="text-white/30">/</span>
                  <b>{game.current_threshold}</b>
                </>
              ) : (
                tRich("top.threshold", { n: <b>{game.current_threshold}</b> })
              )}
            </div>
          );
        })()}
      </div>

      {/* CENTER: bouton paramètres de la partie (absolute, centré dans la barre). Below 400 px it
          takes its place in the row, icon only: centred, it covered the threshold. */}
      <div className="min-[400px]:absolute min-[400px]:left-1/2 min-[400px]:-translate-x-1/2">
        <Popover open={paramsOpen} onOpenChange={setParamsOpen}>
          <PopoverTrigger asChild>
            <button
              title={t("top.paramsTitle")}
              data-testid="game-params-trigger"
              className={`rami-glass rounded-full flex items-center gap-1 px-2.5 py-1 text-[10px] sm:text-xs font-medium transition-colors duration-200 ${
                paramsOpen
                  ? "text-amber-300 border border-amber-400/50"
                  : "text-amber-400/60 hover:text-amber-300 border border-transparent"
              }`}
            >
              <Info className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden min-[400px]:inline">{t("top.infos")}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="center"
            className="w-64 bg-slate-900 border-slate-700 text-slate-200 p-3 shadow-xl"
          >
            <GameParamsPanel game={game} />
          </PopoverContent>
        </Popover>
      </div>

      {/* RIGHT: pending counter + audio + voice + exit */}
      <div className="flex items-center gap-1 sm:gap-1.5 text-xs">
        {/* Voice chat — hidden for now: see VOICE_CHAT in lib/features.js */}
        {VOICE_CHAT && (
          <>
            <Button
              variant="ghost"
              data-testid="toggle-sound-btn"
              onClick={handleToggleSound}
              size="sm"
              className="text-slate-300 h-8 px-1.5 sm:px-2"
              aria-label={voiceInputMuted ? t("top.soundOn") : t("top.soundOff")}
            >
              {voiceInputMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </Button>
            <VoiceChat
              wsRef={wsRef}
              wsVersion={wsVersion}
              selfUserId={user.user_id}
              players={game.players}
              inputMuted={voiceInputMuted}
              onSpeakingChange={handleVoiceSpeaking}
              onLinksChange={onVoiceLinksChange}
              onMicOn={handleMicOn}
            />
          </>
        )}
        <Button
          variant="ghost"
          data-testid="exit-game-btn"
          onClick={onExit}
          size="sm"
          className="text-slate-300 h-8 px-1.5 sm:px-2"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default GameTopBar;
